'use client'

import React from 'react'

import {
  mediaCarouselColumnStyles,
  mediaCarouselDotStyles,
  mediaCarouselDotsRowStyles,
  mediaCarouselFrameStyles,
  mediaCarouselMediaBoxStyles,
  mediaCarouselNavZoneStyles,
} from './MediaCarousel.styles'

import { Box, Skeleton, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { ChevronLeftIcon } from '../Icon/ChevronLeftIcon'
import { ChevronRightIcon } from '../Icon/ChevronRightIcon'

export type MediaCarouselProps = {
  /** Zero-based index of the current slide. */
  index: number
  /** Total number of slides; navigation and the pager hide when below 2. */
  itemCount: number
  isLoading?: boolean
  onPrevious?: () => void
  onNext?: () => void
  colorScheme?: ColorScheme
  /** The current slide's media. The carousel renders one slide at a time. */
  children: React.ReactNode
  /** Frame width; the media area sizes to it. */
  width?: string
}

/**
 * Framed single-slide media carousel (ENG-32107): bordered frame with a
 * skeleton fill while loading, full-height margin navigation zones (clicking
 * the margin advances, hover scrims the edge and brightens the chevron), and
 * an in-flow dot pager beneath the media. Navigation at the ends is omitted
 * rather than disabled, and a zone renders only when its handler is provided
 * — no dead controls. The slide itself comes through `children` — the
 * consumer owns media types, preview wiring, and slide state.
 */
export const MediaCarousel = ({
  index,
  itemCount,
  isLoading = false,
  onPrevious,
  onNext,
  colorScheme = 'dark',
  children,
  width = '100%',
}: MediaCarouselProps) => {
  const hasMultiple = itemCount > 1
  const isFirstSlide = index <= 0
  const isLastSlide = index >= itemCount - 1

  return (
    <Skeleton
      loading={isLoading}
      {...mediaCarouselFrameStyles(colorScheme, isLoading, width)}
    >
      <Box {...mediaCarouselColumnStyles}>
        <Box {...mediaCarouselMediaBoxStyles}>
          {/* No control at the ends — the zone disappears instead of disabling */}
          {hasMultiple && !isFirstSlide && onPrevious && (
            <chakra.button
              aria-label='Previous'
              onClick={onPrevious}
              type='button'
              {...mediaCarouselNavZoneStyles(colorScheme, 'left')}
            >
              <ChevronLeftIcon height={48} width={48} />
            </chakra.button>
          )}
          {hasMultiple && !isLastSlide && onNext && (
            <chakra.button
              aria-label='Next'
              onClick={onNext}
              type='button'
              {...mediaCarouselNavZoneStyles(colorScheme, 'right')}
            >
              <ChevronRightIcon height={48} width={48} />
            </chakra.button>
          )}
          {children}
        </Box>
        {hasMultiple && (
          <Box {...mediaCarouselDotsRowStyles}>
            {Array.from({ length: itemCount }, (_, i) => (
              <Box
                aria-current={index === i || undefined}
                data-testid={
                  index === i ? 'mediaCarouselDotActive' : 'mediaCarouselDot'
                }
                key={i}
                {...mediaCarouselDotStyles(colorScheme, index === i)}
              />
            ))}
          </Box>
        )}
      </Box>
    </Skeleton>
  )
}
